import * as types from './actionTypes';
import * as authTypes from '../user/actionTypes';
import MapClient from '../../focus-client/map-client';
import GeoFeatureClient from '../../focus-client/crud-client/geofeature-client';
import ObservationClient from '../../focus-client/crud-client/observation-client';
import SearchClient from '../../focus-client/search-client';
import QueryBuilder from '../../query-builder';
import { fetchAllFeaturePages } from '../../utils/paginationUtil';

const setProjectLayers = data => ({
  type: types.SET_PROJECT_LAYERS_SUCCESS,
  payload: data
});

const setLayerFeatures = (
  projectId,
  layerId,
  data,
  selectedItem,
  selectedTrackObservations,
  agentRegistry = null
) => ({
  type: types.SET_LAYER_FEATURES_SUCCESS,
  payload: {
    projectId,
    layerId,
    data,
    selectedItem,
    selectedTrackObservations,
    agentRegistry
  }
});

// Stores the parsed MarkLogic box-grid heatmap (GeoJSON of graduated
// cells) for a given layer name (e.g. 'Observations' / 'Tracks').
const setLayerHeatmap = (layerName, data) => ({
  type: types.SET_LAYER_HEATMAP_SUCCESS,
  payload: { layerName, data }
});

const setAlertFeatures = (
  projectId,
  layerId,
  data,
  selectedItem,
  selectedTrackObservations
) => ({
  type: types.SET_ALERT_FEATURES_SUCCESS,
  payload: { projectId, layerId, data, selectedItem, selectedTrackObservations }
});

const getEntireTrack = (projectId, layerId, searchQuery) => {
  var config = {
    baseURI: document.baseURI,
    searchType: 'all'
  };

  return dispatch => {
    let map = MapClient(config);

    // Fetches every page of this track's trace, not just the first 5000
    // points - see paginationUtil.js.
    return fetchAllFeaturePages(page =>
      map.features(
        null,
        projectId,
        layerId,
        searchQuery,
        page.start,
        page.pageLength
      )
    ).then(response => {
      if (!response.isError) {
        dispatch(setEntireTrack(response));
      } else if (response.error.status === 401) {
        dispatch(localLogout());
      }
    });
  };
};

const getTrackInfo = geoFeatureId => {
  var config = {
    baseURI: document.baseURI,
    searchType: 'all'
  };

  return (dispatch, getState) => {
    let geoFeature = GeoFeatureClient(config);

    return geoFeature
      .read(geoFeatureId, getState().ontology.selectedLanguage)
      .then(response => {
        if (!response.isError) {
          if (response.envelope) dispatch(setTrackInfo(response.envelope));
        } else if (response.error.status === 401) {
          dispatch(localLogout());
        }
      });
  };
};

const getTrackObservations = geoFeatureId => {
  var config = {
    baseURI: document.baseURI,
    searchType: 'all'
  };

  return (dispatch, getState) => {
    let geoFeature = GeoFeatureClient(config);
    let observation = ObservationClient(config);
    const language = getState().ontology.selectedLanguage;
    const firstPageLength = 250;
    const maxParallelRequests = 3;

    /**
     * Fire-and-forget: fetches media for each observation in a results page
     * and dispatches SET_TRACK_OBSERVATIONS_MEDIA so the camera badge count
     * shows correctly in linked-track rows — matching the behaviour of the
     * normal timeline (see timeline/actions.js getTimeline).
     * @param {Array} results - Page of { Observation } entries from the API.
     */
    const fetchMediaForResults = results => {
      results.forEach(entry => {
        const observationId =
          entry && entry.Observation && entry.Observation.observationId;
        if (!observationId) return;
        observation
          .media(observationId, language)
          .then(mediaResponse => {
            if (Array.isArray(mediaResponse) && mediaResponse.length > 0) {
              dispatch(setTrackObservationMedia(observationId, mediaResponse));
            }
          })
          .catch(() => {
            // Non-fatal: media count simply stays empty for this observation.
          });
      });
    };

    const parseResponse = response => {
      if (Array.isArray(response)) {
        return {
          results: response,
          total: response.length,
          start: 1,
          pageLength: response.length || firstPageLength
        };
      }
      const results =
        response && Array.isArray(response.results) ? response.results : [];
      return {
        results,
        total: Number(response && response.total) || results.length,
        start: Number(response && response.start) || 1,
        pageLength:
          Number(response && response.pageLength) ||
          Number(response && response['page-length']) ||
          firstPageLength
      };
    };

    return geoFeature
      .observations(geoFeatureId, language, {
        start: 1,
        pageLength: firstPageLength
      })
      .then(async response => {
        if (!response.isError) {
          const firstPage = parseResponse(response);
          dispatch(setTrackObservations(firstPage.results));
          fetchMediaForResults(firstPage.results);
          dispatch(
            setTrackObservationCount({
              trackId: String(geoFeatureId),
              count: Number(firstPage.total) || 0
            })
          );

          const total = firstPage.total;
          const pageLength = Math.max(
            1,
            Number(firstPage.pageLength) || firstPageLength
          );
          if (total <= firstPage.results.length) {
            return;
          }

          const remainingStarts = [];
          for (
            let start = firstPage.start + pageLength;
            start <= total;
            start += pageLength
          ) {
            remainingStarts.push(start);
          }

          const runBatch = async batchStarts => {
            const results = await Promise.all(
              batchStarts.map(start =>
                geoFeature.observations(geoFeatureId, language, {
                  start,
                  pageLength
                })
              )
            );
            results.forEach(pageResponse => {
              if (pageResponse && !pageResponse.isError) {
                const page = parseResponse(pageResponse);
                if (page.results.length) {
                  dispatch(appendTrackObservations(page.results));
                  fetchMediaForResults(page.results);
                }
              }
            });
          };

          for (
            let index = 0;
            index < remainingStarts.length;
            index += maxParallelRequests
          ) {
            const batchStarts = remainingStarts.slice(
              index,
              index + maxParallelRequests
            );
            // Fetch remaining pages in parallel batches to keep UI responsive.
            // eslint-disable-next-line no-await-in-loop
            await runBatch(batchStarts);
          }
        } else if (response.error && response.error.status === 401) {
          dispatch(localLogout());
        }
      });
  };
};

const getTrackObservationsCount = geoFeatureId => {
  var config = {
    baseURI: document.baseURI,
    searchType: 'all'
  };

  return (dispatch, getState) => {
    let geoFeature = GeoFeatureClient(config);
    return geoFeature
      .observations(geoFeatureId, getState().ontology.selectedLanguage, {
        start: 1,
        pageLength: 1
      })
      .then(response => {
        if (!response.isError) {
          const total = Array.isArray(response)
            ? response.length
            : Number(response && response.total) ||
              (response && Array.isArray(response.results)
                ? response.results.length
                : 0);
          dispatch(
            setTrackObservationCount({
              trackId: String(geoFeatureId),
              count: Math.max(0, Number(total) || 0)
            })
          );
        } else if (response.error && response.error.status === 401) {
          dispatch(localLogout());
        }
      });
  };
};

const setSelectedLayers = value => ({
  type: types.SET_SELECTED_LAYERS,
  payload: value
});

const localLogout = () => ({
  type: authTypes.LOCAL_LOGOUT
});

const setEntireTrack = value => ({
  type: types.SET_ENTIRE_TRACK,
  payload: value
});

const setFullTrackTrace = (trackId, data) => ({
  type: types.SET_FULL_TRACK_TRACE,
  payload: { trackId, data }
});

// Fetches the full GPS trace for a single track (same entityId-scoped fetch
// as getEntireTrack), but merges it into the fullTrackTraces cache instead
// of overwriting trackLayer - so many tracks' full traces can be held at
// once. Used by the "Full tracks - standard" display mode, which needs the
// real route for every track currently visible on the map, not just the
// single selected one.
const getTrackFullTrace = trackId => {
  const config = {
    baseURI: document.baseURI,
    searchType: 'all'
  };

  return dispatch => {
    const map = MapClient(config);
    const query = new QueryBuilder.SearchQuery().withQueryText(
      'entityId:"' + trackId + '"'
    );

    // Fetches every page of this track's trace, not just the first 5000
    // points - see paginationUtil.js.
    return fetchAllFeaturePages(page =>
      map.features(null, 'track', '0', query, page.start, page.pageLength)
    ).then(response => {
      if (!response.isError) {
        dispatch(setFullTrackTrace(trackId, response));
      } else if (response.error && response.error.status === 401) {
        dispatch(localLogout());
      }
    });
  };
};

const setTrackInfo = value => ({
  type: types.SET_TRACK_INFO,
  payload: value
});

const setTrackObservations = value => ({
  type: types.SET_TRACK_OBSERVATIONS,
  payload: value
});

const appendTrackObservations = value => ({
  type: types.APPEND_TRACK_OBSERVATIONS,
  payload: value
});

const setTrackObservationCount = value => ({
  type: types.SET_TRACK_OBSERVATION_COUNT,
  payload: value
});

/**
 * Dispatched after media is fetched for a single linked track observation.
 * @param {string} observationId - The observation's ID.
 * @param {Array}  values        - Array of media objects returned by the API.
 */
const setTrackObservationMedia = (observationId, values) => ({
  type: types.SET_TRACK_OBSERVATIONS_MEDIA,
  payload: { observationId, values }
});

const clearEntireTrack = () => ({
  type: types.CLEAR_ENTIRE_TRACK,
  payload: null
});

const clearCommonLayerFeatures = () => ({
  type: types.CLEAR_COMMON_LAYER_FEATURES,
  payload: null
});

const setCamtrapFocus = ids => ({
  type: types.SET_CAMTRAP_FOCUS,
  payload: ids
});

const clearCamtrapFocus = () => ({
  type: types.CLEAR_CAMTRAP_FOCUS
});

const setCamtrapResults = results => ({
  type: types.SET_CAMTRAP_RESULTS,
  payload: results
});

const setCamtrapObservationMedia = (observationId, values) => ({
  type: types.SET_CAMTRAP_OBSERVATION_MEDIA,
  payload: { observationId, values }
});

const fetchClusterResults = memberEntityIds => {
  const config = { baseURI: document.baseURI, searchType: 'all' };
  return (dispatch, getState) => {
    if (!Array.isArray(memberEntityIds) || memberEntityIds.length === 0) return;
    dispatch(setCamtrapResults([]));
    const state = getState();
    const idQuery = memberEntityIds.map(id => `entityId:"${id}"`).join(' OR ');
    const pageLength = memberEntityIds.length + 10;
    const query = new QueryBuilder.SearchQuery()
      .withQueryText(idQuery)
      .withOptions(new QueryBuilder.SearchOptions(0, pageLength));
    query.withEntities(['Observation'], 'or');
    if (state.query.start !== null && state.query.end !== null)
      query.withDateTimeRange(state.query.end, state.query.start);

    const observation = ObservationClient(config);
    const search = SearchClient(config);
    return search
      .results(query, state.ontology.selectedLanguage, 'fetch-cluster')
      .then(response => {
        if (!response.isError) {
          dispatch(setCamtrapResults(response.results || []));
          (response.results || []).forEach(item => {
            const hasObservation = (item.extracted.content || []).some(
              c => !!c.Observation
            );
            if (!hasObservation) return;
            observation
              .media(item.id, getState().ontology.selectedLanguage)
              .then(mediaResponse => {
                if (Array.isArray(mediaResponse) && mediaResponse.length > 0) {
                  dispatch(setCamtrapObservationMedia(item.id, mediaResponse));
                }
              })
              .catch(() => {});
          });
        }
      });
  };
};

export default {
  setProjectLayers,
  setLayerFeatures,
  setLayerHeatmap,
  setAlertFeatures,
  getEntireTrack,
  getTrackInfo,
  getTrackObservations,
  getTrackObservationsCount,
  clearEntireTrack,
  clearCommonLayerFeatures,
  setSelectedLayers,
  getTrackFullTrace,
  setCamtrapFocus,
  clearCamtrapFocus,
  fetchClusterResults
};
